import { Injectable, inject, WritableSignal } from '@angular/core';
import { Subscription } from 'rxjs';
import { NavigationService } from '../core/navigation.service';
import { WaypointService } from '../core/waypoint.service';
import { MapService } from '../map/map.service';
import { WebviewService } from '../platform/webview.service';
import { EvCalculationService } from '../core/ev-calculation.service';
import { EvCalculationFlowHandler } from './ev-calculation-flow-handler.service';
import { MapComponent } from '../../components/map/map.component';
import { Waypoint } from '../../models/range-params';
import { DisplayMode } from '../../models/display-mode';

/**
 * MainComponent implements this interface and passes itself to bind().
 * The handler reads/writes the three mutable navigation flags, delegates
 * change detection and NoSleep toggling back to the host (where the
 * device-detection logic and ViewChild references already live).
 */
export interface NavigationLifecycleHost {
  readonly mapComponent?: MapComponent;
  readonly isNavigating: WritableSignal<boolean>;
  readonly isNavigationRecalculating: WritableSignal<boolean>;
  readonly lastWaypoints: WritableSignal<Waypoint[] | null>;
  enableNoSleep(): void;
  disableNoSleep(): void;
}

/**
 * Owns the navigation lifecycle subscription (start / exit / arrive).
 * Split from MainComponent because the 70-line ObservableChangeNavigation
 * handler is fully self-contained and only touches injectable services plus
 * a small set of host flags.
 */
@Injectable({ providedIn: 'root' })
export class NavigationLifecycleHandler {
  private host: NavigationLifecycleHost | null = null;
  private sub: Subscription | null = null;

  private readonly navigationService = inject(NavigationService);
  private readonly waypointService = inject(WaypointService);
  private readonly mapService = inject(MapService);
  private readonly webviewService = inject(WebviewService);
  private readonly evCalculationService = inject(EvCalculationService);
  private readonly flowHandler = inject(EvCalculationFlowHandler);

  bind(host: NavigationLifecycleHost): void {
    this.host = host;
    if (!this.sub) {
      this.sub = this.navigationService.ObservableChangeNavigation.subscribe(resp => {
        if (resp != null) this.handleNavChange(resp);
      });
    }
  }

  unbind(): void {
    this.sub?.unsubscribe();
    this.sub = null;
    this.host = null;
  }

  private handleNavChange(resp: string): void {
    if (!this.host) return;

    if (resp === 'start' && !this.host.isNavigating()) {
      if (this.host.lastWaypoints() == null) {
        this.host.lastWaypoints.set(this.waypointService.getWaypointsParams());
      }
      this.host.isNavigationRecalculating.set(true);
      this.host.isNavigating.set(true);
      this.mapService.ObservableActivePlanningPanelRouteElement.next({ type: 'clear' });
      this.flowHandler.startBetterRouteTimer();
      this.host.enableNoSleep();
      document.documentElement.classList.add('navigation');

      this.navigationService.LastReplanTime = Date.now();
      this.evCalculationService.requestNavigationStart();
      this.waypointService.setSelectedDisplayMode(DisplayMode.NAVIGATION);
      return;
    }

    if (resp === 'exit') {
      if (this.webviewService.IsGPSTWebview()) {
        this.webviewService.StopNavigation();
      }
      this.navigationService.ObservableCannotSkipMoreChargingStation.next(false);
      localStorage.setItem('skippedChargingStation', 'null');
      this.host.isNavigationRecalculating.set(false);
      this.host.isNavigating.set(false);
      this.waypointService.setSelectedDisplayMode(DisplayMode.RANGE);
      this.flowHandler.stopBetterRouteTimer();
      if (this.host.mapComponent) {
        this.host.mapComponent.exitNavigation();
      }
      // Drop the route and keep only the current start waypoint, so the
      // pipeline dispatches a range calculation (which also clears the
      // route from the map) instead of re-planning the abandoned route.
      this.waypointService.setWaypointParams([this.waypointService.getWaypointsParams()[0]]);
      this.host.disableNoSleep();
      this.host.lastWaypoints.set(null);
      document.documentElement.classList.remove('navigation');
      return;
    }

    if (resp === 'arrive') {
      if (this.webviewService.IsGPSTWebview()) {
        this.webviewService.StopNavigation();
      }
      this.navigationService.ObservableCannotSkipMoreChargingStation.next(false);
      localStorage.setItem('skippedChargingStation', 'null');
      this.waypointService.setSelectedDisplayMode(DisplayMode.RANGE);
      this.host.isNavigating.set(false);
      this.flowHandler.stopBetterRouteTimer();
      if (this.host.mapComponent) {
        this.host.mapComponent.exitNavigation();
      }
      this.waypointService.setWaypointParams([
        this.waypointService.getWaypointsParams()[this.waypointService.getWaypointsParams().length - 1],
      ]);
      this.host.disableNoSleep();
      this.host.lastWaypoints.set(null);
      document.documentElement.classList.remove('navigation');
    }
  }
}